import { useMutation, useQuery } from '@apollo/client';
import React, { useRef, useState } from 'react';

import { Icon, IconDelete } from '@flixbus/honeycomb-icons-react';
import {
	Box,
	Button,
	Heading,
	Panel,
	PanelContent,
	PanelHeader,
	Popup,
	PopupSection,
	Switch,
	Tag,
	Text,
} from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import { formatDateDisplay, isTimestampBefore, sortByDateUsingTimestamp } from 'utils/datetime';

import { DELETE_LOCATION } from './gql/deleteLocationsLogEntry';
import { FETCH_LOCATIONS_LOG } from './gql/fetchLocationsLog';

import styles from './styles.module.css';

/**
 * PROPS (ActiveElements + page):
 * - employeeId: string (required)
 * - currentHomeBase: { id, effectiveTimestamp } (required for “current” calc)
 * - onClose: () => void
 * - onError?: () => void
 */
const LocationsLog = ({ employeeId, currentHomeBase, onClose, onError = () => {} }) => {
	const targetRef = useRef(null);

	const [homeLocations, setHomeLocations] = useState([]);
	const [warnModalShown, setWarnModalShown] = useState(false);
	const [currentlyDeleting, setCurrentlyDeleting] = useState('');
	const [noExpiredBases, setNoExpiredBases] = useState(true);
	const [expiredShown, setExpiredShown] = useState(false);

	const { loading: queryLoading } = useQuery(FETCH_LOCATIONS_LOG, {
		variables: { crewMemberId: employeeId },
		onCompleted: (data) => {
			let noExpired = true;
			const { homeBases } = data.crewMemberHomeBases;
			const currentId = currentHomeBase?.id;
			const currentEffective = currentHomeBase?.effectiveTimestamp;

			const sorted = [...homeBases]
				.sort((a, b) => sortByDateUsingTimestamp(a.effectiveTimestamp, b.effectiveTimestamp))
				.map((hb) => {
					const { effectiveTimestamp, expireTimestamp, city, id } = hb;

					const isCurrent = id === currentId;
					const isBeforeNow = isTimestampBefore(
						expireTimestamp !== null ? expireTimestamp : effectiveTimestamp,
						expireTimestamp !== null ? new Date() : (currentEffective ?? new Date())
					);

					if (isBeforeNow && !isCurrent) noExpired = false;

					return {
						id,
						city: city.name,
						effectiveTimestamp: formatDateDisplay(effectiveTimestamp),
						expireTimestamp: expireTimestamp ? formatDateDisplay(expireTimestamp) : null,
						isExpired: isBeforeNow && !isCurrent,
					};
				});

			setHomeLocations(sorted);
			setNoExpiredBases(noExpired);
		},
		onError: () => onError(),
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const [deleteHomeBase, { loading: mutationLoading }] = useMutation(DELETE_LOCATION, {
		refetchQueries: ['GetCrewMemberHomeBases'],
		onCompleted: () => setCurrentlyDeleting(''),
		onError: () => onError(),
		fetchPolicy: 'no-cache',
	});

	const warnDeletion = (id) => {
		setWarnModalShown(true);
		setCurrentlyDeleting(id);
	};

	const onDeleteHomebase = () => {
		setWarnModalShown(false);
		deleteHomeBase({ variables: { baseId: currentlyDeleting } });
	};

	const onCancelDeletion = () => {
		setWarnModalShown(false);
		setCurrentlyDeleting('');
	};

	const getLabelForLocation = (isCurrent, isExpired) => {
		if (isCurrent) return 'Current';
		if (isExpired) return 'Past';
		return 'Future';
	};

	const renderLocations = () => {
		if (queryLoading) return null;
		if (homeLocations.length === 0)
			return <div>No home location was set for this crew member.</div>;

		return homeLocations
			.filter((homeBase) => homeBase != null)
			.map((homeBase) => {
				const { isExpired, id, city, effectiveTimestamp, expireTimestamp } = homeBase;
				if (isExpired && !expiredShown) return null;

				const isCurrent = currentHomeBase != null && id === currentHomeBase.id;

				return (
					<Box key={id} extraClasses={styles.boxpadding}>
						<div className={styles.row}>
							<Heading extraClasses={styles.heading} size={4} flushSpace>
								{city}
							</Heading>
							<Tag display="subtle" {...(isCurrent ? { appearance: 'success' } : {})} small>
								{getLabelForLocation(isCurrent, isExpired)}
							</Tag>

							{!isCurrent && !isExpired && (
								<Button
									display="square"
									aria-label="Delete entry"
									appearance="link"
									onClick={() => warnDeletion(id)}
									loading={mutationLoading && currentlyDeleting === id}
								>
									{!mutationLoading && <Icon InlineIcon={IconDelete} aria-hidden="true" size={4} />}
								</Button>
							)}
						</div>

						<div className={styles.row}>
							<span className={styles.date}>
								Effective date:
								<br /> {effectiveTimestamp}
							</span>

							{expireTimestamp !== null && (
								<span className={styles.date}>
									Expiration date:
									<br /> {expireTimestamp}
								</span>
							)}
						</div>
					</Box>
				);
			});
	};

	return (
		<>
			<Panel
				id="locations-panel"
				data-testid="panel"
				active
				hasOverlay
				onOverlayClick={onClose}
				targetRef={targetRef}
			>
				<PanelHeader closeButtonProps={{ label: 'Close', onClick: onClose }}>
					Home locations log
				</PanelHeader>

				<PanelContent>
					<Switch
						extraClasses={styles.toggle}
						disabled={noExpiredBases}
						label="Show expired locations"
						id="switch-expired"
						value="expired"
						onChange={() => setExpiredShown((v) => !v)}
					/>

					{renderLocations()}
					{queryLoading && <Loader />}
				</PanelContent>
			</Panel>

			<Popup id="delete-location" style={{ zIndex: '10000' }} active={warnModalShown}>
				<PopupSection>
					<h1>Confirm deletion</h1>
				</PopupSection>
				<PopupSection>
					<Text>You're about to delete this item permanently. Do you want to proceed?</Text>
				</PopupSection>
				<PopupSection>
					<Button onClick={onCancelDeletion}>Cancel</Button>
					<Button onClick={onDeleteHomebase} appearance="danger">
						Delete
					</Button>
				</PopupSection>
			</Popup>
		</>
	);
};

export default LocationsLog;
